import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, ChevronLeft, Download, ChevronRight } from "lucide-react";
import { INSTRUCTIVOS, instructivoPorId } from "@/instructivos/contenido";

// "**texto**" → negritas. Nada más: los instructivos son cortos a propósito.
const Texto = ({ children }) => (
  <>
    {children.split("**").map((t, i) => (i % 2 ? <strong key={i} className="font-semibold text-slate-900">{t}</strong> : t))}
  </>
);

// Descargar = imprimir a PDF con el navegador. Espera a que carguen las capturas y usa el título como nombre del archivo.
const descargarPdf = async (titulo) => {
  const anterior = document.title;
  document.title = `PLM - ${titulo}`;
  await Promise.all(Array.from(document.images).filter((im) => !im.complete).map((im) => new Promise((r) => { im.onload = im.onerror = r; })));
  window.print();
  document.title = anterior;
};

function Instructivo({ ins, primero = true }) {
  return (
    <section className={primero ? "" : "break-before-page pt-2"} data-testid={`instructivo-${ins.id}`}>
      <div className="mb-5">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">{ins.titulo}</h1>
        <p className="text-sm text-slate-500 mt-1">{ins.resumen} · Para: {ins.para}</p>
      </div>
      <ol className="space-y-6">
        {ins.pasos.map((p, i) => (
          <li key={p.img} className="break-inside-avoid" data-testid={`instructivo-paso-${ins.id}-${i + 1}`}>
            <div className="flex items-start gap-3 mb-2">
              <span className="h-7 w-7 shrink-0 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center print:border print:border-blue-600">{i + 1}</span>
              <p className="text-[15px] text-slate-700 leading-snug pt-0.5"><Texto>{p.texto}</Texto></p>
            </div>
            <img
              src={`/instructivos/${ins.id}/${p.img}.webp`}
              alt={`Paso ${i + 1}`}
              className="w-full max-w-3xl rounded-lg border border-slate-200 shadow-sm print:shadow-none print:max-h-[42vh] print:w-auto print:max-w-full"
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function Instructivos() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const todos = id === "todos";
  const ins = id && !todos ? instructivoPorId(id) : null;
  const titulo = todos ? "Manual de uso" : ins?.titulo;

  // ?pdf=1 (desde "Descargar manual completo") abre el diálogo de impresión en cuanto se pinta la página.
  useEffect(() => {
    if (params.get("pdf") === "1" && titulo) descargarPdf(titulo);
  }, [params, titulo]);

  if (!id) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Instructivos</h1>
            <p className="text-sm text-slate-500 mt-1">Guías paso a paso con imágenes. Cada una se puede descargar en PDF.</p>
          </div>
          <Button asChild variant="outline" data-testid="instructivos-descargar-todos">
            <Link to="/instructivos/todos?pdf=1"><Download size={16} className="mr-2" />Descargar manual completo</Link>
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="instructivos-lista">
          {INSTRUCTIVOS.map((i) => (
            <Link key={i.id} to={`/instructivos/${i.id}`} data-testid={`instructivo-card-${i.id}`}>
              <Card className="p-5 h-full border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><BookOpen size={20} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800">{i.titulo}</div>
                    <p className="text-sm text-slate-500 mt-1">{i.resumen}</p>
                    <p className="text-xs text-slate-400 mt-2">Para: {i.para} · {i.pasos.length} pasos</p>
                  </div>
                  <ChevronRight size={18} className="text-slate-300 mt-2" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 print:p-0">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3 print:hidden">
        <Link to="/instructivos" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors">
          <ChevronLeft size={16} /> Instructivos
        </Link>
        {titulo && (
          <Button onClick={() => descargarPdf(titulo)} data-testid="instructivo-descargar">
            <Download size={16} className="mr-2" />Descargar PDF
          </Button>
        )}
      </div>
      <p className="hidden print:block text-xs text-slate-400 mb-3">PLM Aretina · {titulo}</p>
      {todos
        ? INSTRUCTIVOS.map((i, n) => <Instructivo key={i.id} ins={i} primero={n === 0} />)
        : ins
          ? <Instructivo ins={ins} />
          : <p className="text-slate-500">No existe ese instructivo.</p>}
    </div>
  );
}
